import { schemaModel } from '@scalar-org/entities/utility'
import type { DocumentPrimitive, FieldQuery } from '@scalar-org/ts-helpers'
import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { type ZodSchema, type ZodTypeDef, z } from 'zod'

// Mirrored from mongobase/domains/database/api/routes

const primitiveSchema: z.ZodType<DocumentPrimitive> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
])

const $filterSchema: z.ZodType<FieldQuery> = z.object({
  $in: z.string().array().optional(),
  $nin: z.string().array().optional(),
  $eq: primitiveSchema.optional(),
  $ne: primitiveSchema.optional(),
  $lt: z.number().optional(),
  $gt: z.number().optional(),
  $gte: z.number().optional(),
  $lte: z.number().optional(),
  $or: z.lazy(() => $filterSchema.array()).optional(),
})

type MongobaseQuery = Record<string, FieldQuery | DocumentPrimitive>

const querySchema = z.record(
  z.string(),
  z.union([primitiveSchema, $filterSchema]),
)

const getRecordRequestSchema = z.object({
  collectionPath: z.string(),
  query: querySchema,
})

const addRecordRequestSchema = z.object({
  collectionPath: z.string(),
  data: z.record(z.string(), z.any()),
})

const addMultipleRecordsRequestSchema = z.object({
  collectionPath: z.string(),
  data: z.record(z.string(), z.any()).array(),
})

const updateRecordRequestSchema = z.object({
  collectionPath: z.string(),
  data: z.record(z.string(), z.any()),
  query: querySchema,
})

const deleteRecordRequestSchema = z.object({
  collectionPath: z.string(),
  query: querySchema,
})

const dropCollectionRequestSchema = z.object({
  collectionPath: z.string(),
})

// ---------------------------------------------------------------------------

type DBResponse<T> = {
  status: number
  data: T
  error: false
}

function formatResponse<T>(
  response: AxiosResponse<any>,
  schema: ZodSchema<T, ZodTypeDef, any>,
): DBResponse<T> {
  return {
    status: response.status,
    data: schemaModel(response.data, schema),
    error: false,
  }
}

type DBError = {
  status: number
  message: string
  error: true
  originalError: AxiosError
}

const statusMessages: Record<string | number, string> = {
  401: 'Unauthorized. Please check that an access token was provided to the instance. ',
}
function formatError(error: AxiosError, message?: string): DBError {
  return {
    status: error.response?.status ?? 500,
    message:
      message ??
      statusMessages[error.response?.status as number] ??
      error.message,
    error: true,
    originalError: error,
  }
}

const addResponseSchema = z.object({
  insertedId: z.string(),
})

const addMultiResponseSchema = z.object({
  insertedCount: z.number(),
  insertedIds: z.preprocess(
    (s) => (Array.isArray(s) ? s : Object.values(s as any)),
    z.string().array(),
  ),
})

const updateResponseSchema = z.object({
  matchedCount: z.number(),
  modifiedCount: z.number(),
  upsertedCount: z.number(),
  upsertedId: z.string().nullable(),
})

const deletedResponseSchema = z.object({
  deletedCount: z.number(),
})

// ---------------------------------------------------------------------------

/** Initialize database access */
export function createAccess(baseURL: string) {
  const state = {
    accessToken: '',
  }

  const instance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  const createHeaders = () => ({
    Authorization: `Bearer ${state.accessToken}`,
  })

  /** Get one or more items from the database */
  function get<T>({
    collectionPath,
    query,
    schema,
    type,
  }: {
    collectionPath: string
    query: MongobaseQuery
    schema: ZodSchema<T, ZodTypeDef, any>
    type: 'one' | 'many'
  }) {
    const data = schemaModel({ collectionPath, query }, getRecordRequestSchema)

    return instance
      .request({
        url: `/database/get-${type}`,
        method: 'POST',
        headers: createHeaders(),
        data,
      })
      .then((res) => formatResponse(res, schema))
      .catch((err) => formatError(err))
  }

  /** Add one or more records to the database */
  function addOne<T extends Record<string, any>, R>({
    collectionPath,
    data,
    schema,
    responseSchema,
  }: {
    collectionPath: string
    data: T
    schema: ZodSchema<T, ZodTypeDef, any>
    responseSchema: ZodSchema<R, ZodTypeDef, any>
  }) {
    const sanitizedData = schemaModel(
      { collectionPath, data: schemaModel(data, schema) },
      addRecordRequestSchema,
    )

    return instance
      .request({
        url: '/database/add-one',
        method: 'POST',
        headers: createHeaders(),
        data: sanitizedData,
      })
      .then((res) => formatResponse(res, responseSchema))
      .catch((err) => formatError(err))
  }

  /** Add multiple records to the database */
  function addMultiple<T extends Record<string, any>[], R>({
    collectionPath,
    data,
    schema,
    responseSchema,
  }: {
    collectionPath: string
    data: T
    schema: ZodSchema<T, ZodTypeDef, any>
    responseSchema: ZodSchema<R, ZodTypeDef, any>
  }) {
    const sanitizedData = schemaModel(
      { collectionPath, data: schemaModel(data, schema) },
      addMultipleRecordsRequestSchema,
    )

    return instance
      .request({
        url: '/database/add-many',
        method: 'POST',
        headers: createHeaders(),
        data: sanitizedData,
      })
      .then((res) => formatResponse(res, responseSchema))
      .catch((err) => formatError(err))
  }

  function remove({
    collectionPath,
    query,
    type,
  }: {
    collectionPath: string
    query: MongobaseQuery
    type: 'one' | 'many'
  }) {
    const data = schemaModel(
      { collectionPath, query },
      deleteRecordRequestSchema,
    )

    return instance
      .request({
        url: `/database/delete-${type}`,
        method: 'POST',
        headers: createHeaders(),
        data,
      })
      .then((res) => formatResponse(res, deletedResponseSchema))
      .catch((err) => formatError(err))
  }

  function update<T>({
    collectionPath,
    data,
    query,
    schema,
  }: {
    collectionPath: string
    data: Partial<T>
    query: MongobaseQuery
    schema: ZodSchema<Partial<T>, ZodTypeDef, any>
  }) {
    const updateData = schemaModel(
      {
        collectionPath,
        query,
        data: schemaModel(data, schema),
      },
      updateRecordRequestSchema,
    )

    return instance
      .request({
        url: '/database/update-one',
        method: 'POST',
        headers: createHeaders(),
        data: updateData,
      })
      .then((res) => formatResponse(res, updateResponseSchema))
      .catch((err) => formatError(err))
  }

  function dropCollection({ collectionPath }: { collectionPath: string }) {
    const data = schemaModel({ collectionPath }, dropCollectionRequestSchema)

    return instance
      .request({
        url: '/database/drop-collection',
        method: 'POST',
        headers: createHeaders(),
        data,
      })
      .then((res) => formatResponse(res, z.any()))
      .catch((err) => formatError(err))
  }
  // ---------------------------------------------------------------------------
  /** Return the database access interface */
  return {
    /** Update the access token */
    updateAccessToken(accessToken: string) {
      state.accessToken = accessToken
    },

    async checkHealth(url: string) {
      // For not access token we just return a mock error
      if (!state.accessToken) {
        return {
          status: 511,
          message: 'No Access Token',
          error: true,
          originalError: null,
        }
      }

      return await instance
        .request({
          url,
          method: 'GET',
          headers: createHeaders(),
        })
        .then((res) => formatResponse(res, z.any()))
        .catch((err) => formatError(err))
    },

    /** Get a single record */
    getOne<T>(options: {
      collectionPath: string
      query: MongobaseQuery
      schema: ZodSchema<T, ZodTypeDef, any>
    }) {
      return get({ ...options, type: 'one' })
    },

    /** Get multiple records */
    getMany<T>(options: {
      collectionPath: string
      query: MongobaseQuery
      schema: ZodSchema<Array<T>, ZodTypeDef, any>
    }) {
      return get({ ...options, type: 'many' })
    },

    /** Add a single record */
    addOne<T extends Record<string, any>>(options: {
      collectionPath: string
      data: T
      schema: ZodSchema<T, ZodTypeDef, any>
    }) {
      return addOne({ ...options, responseSchema: addResponseSchema })
    },

    /** Add multiple records */
    addMany<T extends Record<string, any>>(options: {
      collectionPath: string
      data: Array<T>
      schema: ZodSchema<Array<T>, ZodTypeDef, any>
    }) {
      return addMultiple({ ...options, responseSchema: addMultiResponseSchema })
    },

    updateOne<T>(options: {
      collectionPath: string
      data: Partial<T>
      query: MongobaseQuery
      schema: ZodSchema<Partial<T>, ZodTypeDef, any>
    }) {
      return update({ ...options })
    },

    /** Delete a single record */
    deleteOne(options: {
      collectionPath: string
      query: MongobaseQuery
    }) {
      return remove({ ...options, type: 'one' })
    },

    /** Delete multiple records */
    deleteMany(options: {
      collectionPath: string
      query: MongobaseQuery
    }) {
      return remove({ ...options, type: 'many' })
    },

    dropCollection,
  }
}
